import { ChevronLeftIcon, EnterIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { Badge, Box, Button, Callout, Card, Flex, Spinner, Tabs, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CheckboxGroupComponent from "../../components/inputs/CheckboxGroup/CheckboxGroupComponent";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { useGetPaymentTypesQuery } from "../../hooks/useGetPaymentTypesQuery";
import { useGetSchoolBankConfigQuery } from "../../hooks/useGetSchoolBankConfigQuery";
import { setPdfPreviewType } from "../../redux/slices/report/report";
import { resetStudentData } from "../../redux/slices/school/student";
import { resetSubscriptionData, setSubscriptionData } from "../../redux/slices/school/subscription";
import { resetUserData } from "../../redux/slices/users/user";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { createPaymentReference, getReferenceCodeV2 } from "../../services/finance/paymentReference";
import { createPaymentTransaction } from "../../services/finance/paymentTransaction";
import { createStudentDebt, deleteStudentDebt, getStudentDebtsBySubscription } from "../../services/finance/studentDebt";
import { getPlanSubjects } from "../../services/school/PlanSubjects";
import { createSubscriptionSubject } from "../../services/school/student/createSubscription";
import { getSubjectsSubscriptions } from "../../services/school/student/getSubscriptions";
import { updateSubscription, updateSubscriptionSubject } from "../../services/school/student/updateSubscription"; // API de atualização
import { getSubjectsReprovedQuery } from "../../services/school/subject/getSubjects";
import { PaymentReferenceType, PaymentTransactionType } from "../../types/Finance";
import { SubscriptionSubjectType, SubscriptionType } from "../../types/Student";
import { addDaysToDate, formatDateForInput } from "../../utils/dateFormatter";
import { getFile } from "../../utils/fileUtils";
import { stringToBoolean } from "../../utils/utilities";
import { UserPreview } from "../Users/UserPreview";

export const UpdateSubscription = () => {
  const [activeSegment, setActiveSegment] = useState('user');
  const { data: schoolBankConfigsData, status: schoolBankConfigsStatus, isError: isErrorSchoolschoolBankConfigs, isLoading: isLoadingSchoolschoolBankConfigs } = useGetSchoolBankConfigQuery({ PageNumber: 1, PageSize: 400 });
  const { data: paymentTypesData, status: paymentTypesStatus, isError: isErrorPaymentTypes, isLoading: isLoadingPaymentTypes } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 400 });

  const studentData = useSelector((state: RootState) => state.student.student);
  const userData = useSelector((state: RootState) => state.user);
  const subscriptionData = useSelector((state: RootState) => state.subscription);
  const paymentTotal = subscriptionData?.paymentsReferences?.at(0)?.amount ?? "";
  const { data: currentYear } = useGetCurrentAcademicYearQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mutationUpdateSubscription = useMutation({ mutationFn: updateSubscription, mutationKey: "updateSubscription" });
  const mutationCreateSubscriptionSubject = useMutation({ mutationFn: createSubscriptionSubject, mutationKey: "createSubscriptionSubject" })
  const mutationUpdateSubscriptionSubject = useMutation({ mutationFn: updateSubscriptionSubject, mutationKey: "updateSubscriptionSubject" })
  const loadingState = mutationUpdateSubscriptionSubject.isLoading || mutationCreateSubscriptionSubject.isLoading || mutationUpdateSubscription.isLoading;
  const mutationCreateStudentDebt = useMutation({ mutationFn: createStudentDebt, mutationKey: 'createStudentDebt' });
  const mutationCreatePaymentTransaction = useMutation({ mutationFn: createPaymentTransaction, mutationKey: 'createPaymentTransaction' });
  const mutationSaveReferenceCode = useMutation({ mutationFn: createPaymentReference, mutationKey: 'createPaymentReference' });

  const { data: reprovedSubjectsData, isLoading: isLoadingReprovedSubjects, refetch: fetchReprovedSubjects, isError: isErrorReprovedSubjects, status: reprovedSubjectsStatus } =
    useQuery(
      ['getSubjectsReprovedQuery', studentData?.schoolCode],
      async () => {
        try {
          const data = await getSubjectsReprovedQuery({ PageNumber: 1, PageSize: 400, StudentCode: `${studentData?.schoolCode}`, Year: currentYear?.year, Semester: subscriptionData?.semester });
          return data;
        }
        catch {
        }
      },
      {
        enabled: !!studentData?.schoolCode && !!subscriptionData?.semester,
        refetchOnWindowFocus: false,
        retry: false,
      }
    );
  const { data: planSubjectsData, isLoading: isLoadingPlanSubjectsData, isError: isErrorPlanSubjectsData, refetch: refreshPlanSubjects, status: planSubjectsStatus } =
    useQuery(
      ['getPlanSubjectsQuery'],
      async () => {
        try {
          const data = await getPlanSubjects({
            PageSize: 400,
            PageNumber: 1,
            Level: undefined,
            Semester: subscriptionData?.semester,
            IdPlan: subscriptionData?.idPlan,
            IdCourse: studentData?.idCourse
          }
          );
          const responseData = { ...data }
          responseData.items = responseData.items.filter(i => i.level <= subscriptionData?.level!);
          return responseData;
        } catch { }

      },
      {
        enabled: !!subscriptionData?.idPlan && !!subscriptionData?.level && !!subscriptionData?.semester,
        refetchOnWindowFocus: false,
        retry: false
      }
    );
  type FormValues = {
    retrySubjectsReproved: string[];
    subjectsSubscriptions: string[];
    payment?: {
      isPaymentByEntityReference?: boolean
      entity?: string
      totalPrice?: string | number
      reference?: string,
      idStudentDebt?: string,
      paymentProofSubscription?: any
      paymentProofPerSubject?: any
      paymentProof?: any
      idBankEntity?: string,
      idPaymentTypeSubscription?: string,
      idPaymentTypePerSubject?: string,
    }
  };
  const { control, handleSubmit, setValue, formState: { errors }, watch, getValues, resetField } = useForm<FormValues>({
    // resolver: yupResolver(GetStudentDataFormValidation),
    defaultValues: {
      retrySubjectsReproved: [],
      subjectsSubscriptions: [],
      payment: {
        idPaymentTypeSubscription: subscriptionData?.paymentsReferences?.at(0)?.idPaymentType ?? "",
        isPaymentByEntityReference: subscriptionData?.payment?.isPaymentByEntityReference ?? false,
        idBankEntity: subscriptionData?.payment?.entity ?? "",
        paymentProof: subscriptionData?.payment?.paymentProof ?? "",
      }
    },
    mode: 'onBlur',
  });
  useEffect(() => {
    if (reprovedSubjectsData?.items && subscriptionData?.planSubjects) {
      const selectedReproved = reprovedSubjectsData.items
        .filter(rs => subscriptionData?.planSubjects?.some(cs => cs.idSubject === rs.idSubject))
        .map(item => JSON.stringify(item));
      setValue('retrySubjectsReproved', selectedReproved);
    }

    if (planSubjectsData?.items && subscriptionData?.planSubjects) {
      const selectedRegular = planSubjectsData.items
        .filter(cs => subscriptionData?.planSubjects?.some(sc => sc.idSubject === cs.idSubject))
        .map(item => JSON.stringify({
          id: item.id,
          subject: item.subject,
          semester: item.semester,
          idSubject: item.idSubject,
          idSubjectPrecedence: item.idSubjectPrecedence,
          level: item.level,
        }));

      setValue('subjectsSubscriptions', selectedRegular);
    }
  }, [reprovedSubjectsData, planSubjectsData, subscriptionData, setValue]);

  const removeCurrentDebtAndCreateNewDebt = async (amountToPay: number, data: FormValues, subjects: string[]) => {
    let studentDebtData: any = {
      id: '',
      referenceCode: '',
      paymentProof: ''
    };
    const idsStudentDebtsWithEnrollmentsAssociatate = subscriptionData.studentDebtsAssociations?.filter(s => s?.idEnrollment);
    const idsStudentDebtsWithoutEnrollmentsAssociatate = subscriptionData.studentDebtsAssociations?.filter(s => !s?.idEnrollment && s.idSubscription);


    if (idsStudentDebtsWithEnrollmentsAssociatate && idsStudentDebtsWithEnrollmentsAssociatate.length > 0) {
      console.log("idsStudentDebtsWithEnrollmentsAssociatate ")

      try {
        const res = await Promise.all(idsStudentDebtsWithEnrollmentsAssociatate.map(async isdw => {
          const data = { id: isdw?.idStudentDebt }
          return await deleteStudentDebt(data);
        }))
        console.log("Apagaou o que  ", res)

      } catch (error) {
        console.log(error)
      }
      const enrollmentMoney = idsStudentDebtsWithEnrollmentsAssociatate?.reduce((accumulator, current) => {
        return accumulator + (current?.price || 0);
      }, 0);

      amountToPay = Math.abs(enrollmentMoney - amountToPay);
      studentDebtData = {
        idStudent: studentData?.id ?? "",
        fullname: userData.name.concat(" ").concat(studentData?.schoolCode ?? "") ?? "",
        idPaymentType: data.payment?.idPaymentTypeSubscription ?? data.payment?.idPaymentTypePerSubject ?? "",
        originalAmount: amountToPay,
        currentAmount: amountToPay,
        dueDate: formatDateForInput(addDaysToDate(new Date(), 30).toISOString()),
        isPayed: stringToBoolean(data.payment?.isPaymentByEntityReference) ? false : true,
        description: paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.idPaymentTypeSubscription)?.description ?? paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.paymentProofPerSubject)?.description ?? "N/D",
        idSubscription: subscriptionData.id,
        idEnrollment: idsStudentDebtsWithEnrollmentsAssociatate?.[0]?.idEnrollment,
        idsSubjects: JSON.stringify(subscriptionData?.idsSubjectsArray),
        id: '',
        referenceCode: '',
        paymentProof: ''
      }
      const resultCreateStudentDebt = await mutationCreateStudentDebt.mutateAsync(studentDebtData);
      studentDebtData = { ...studentDebtData, id: resultCreateStudentDebt };
      if (stringToBoolean(getValues('payment.isPaymentByEntityReference'))) {
        const currentDate = new Date();
        try {
          let referenceCode = await getReferenceCodeV2({
            amount: studentDebtData.currentAmount,
            idStudent: studentDebtData.idStudent,
            idPaymentType: studentDebtData.idPaymentType,
            idStudentDebt: studentDebtData.id,
            month: (currentDate.getMonth() + 1).toString(),
            year: currentDate.getFullYear().toString(),
            description: studentDebtData.description,
            idBankEntity: data?.payment?.idBankEntity,
            entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity)
          });
          const paymentReference: PaymentReferenceType = {
            idStudent: studentDebtData.idStudent,
            idPaymentType: studentDebtData.idPaymentType,
            amount: studentDebtData.currentAmount,
            dueDate: new Date(studentDebtData.dueDate).toISOString(),
            referenceCode: referenceCode,
            idStudentDebt: studentDebtData.id,
            entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity),
            idBankEntity: data?.payment?.idBankEntity
          }
          await mutationSaveReferenceCode.mutateAsync(paymentReference)
          studentDebtData.referenceCode = referenceCode;
        } catch (ex: any) {
          toast.error(ex?.message);
        }
      } else {
        const { base64 } = await getFile(data.payment?.paymentProof)
        const paymentTransaction: PaymentTransactionType = {
          date: new Date().toISOString(),
          idPaymentType: data.payment?.idPaymentTypeSubscription!,
          amountPaid: amountToPay ?? 0,
          idStudent: studentDebtData.idStudent,
          paymentProof: base64,
          idStudentDebt: resultCreateStudentDebt,
          idCompleted: true,
        }
        await mutationCreatePaymentTransaction.mutateAsync(paymentTransaction)
        studentDebtData.paymentProof = paymentTransaction.paymentProof;
      }

    }

    if (idsStudentDebtsWithoutEnrollmentsAssociatate && idsStudentDebtsWithoutEnrollmentsAssociatate.length == 0) {
      try {
        const res = await Promise.all(idsStudentDebtsWithoutEnrollmentsAssociatate.map(async isdw => {
          const data = { id: isdw?.idStudentDebt }
          return await deleteStudentDebt(data);
        }))
        if (res.length > 0) {
          studentDebtData = {
            idStudent: studentData?.id ?? "",
            fullname: userData.name.concat(" ").concat(studentData?.schoolCode ?? "") ?? "",
            idPaymentType: data.payment?.idPaymentTypeSubscription ?? data.payment?.idPaymentTypePerSubject ?? "",
            originalAmount: amountToPay,
            currentAmount: amountToPay,
            dueDate: formatDateForInput(addDaysToDate(new Date(), 30).toISOString()),
            isPayed: stringToBoolean(data.payment?.isPaymentByEntityReference) ? false : true,
            description: paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.idPaymentTypeSubscription)?.description ?? paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.idPaymentTypePerSubject)?.description ?? "N/D",
            idSubscription: subscriptionData.id,
            idsSubjects: JSON.stringify(subjects),
            id: '',
            referenceCode: '',
            paymentProof: ''
          }
          const resultCreateStudentDebt = await mutationCreateStudentDebt.mutateAsync(studentDebtData);
          studentDebtData = { ...studentDebtData, id: resultCreateStudentDebt };
          if (stringToBoolean(getValues('payment.isPaymentByEntityReference'))) {
            const currentDate = new Date();
            try {
              let referenceCode = await getReferenceCodeV2({
                amount: studentDebtData.currentAmount,
                idStudent: studentDebtData.idStudent,
                idPaymentType: studentDebtData.idPaymentType,
                idStudentDebt: studentDebtData.id,
                month: (currentDate.getMonth() + 1).toString(),
                year: currentDate.getFullYear().toString(),
                description: studentDebtData.description,
                idBankEntity: data?.payment?.idBankEntity,
                entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity)
              });
              const paymentReference: PaymentReferenceType = {
                idStudent: studentDebtData.idStudent,
                idPaymentType: studentDebtData.idPaymentType,
                amount: studentDebtData.currentAmount,
                dueDate: new Date(studentDebtData.dueDate).toISOString(),
                referenceCode: referenceCode,
                idStudentDebt: studentDebtData.id,
                entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity),
                idBankEntity: data?.payment?.idBankEntity
              }
              await mutationSaveReferenceCode.mutateAsync(paymentReference)
              studentDebtData.referenceCode = referenceCode;

            } catch (ex: any) {
              toast.error(ex?.message);
            }

          } else {
            const { base64 } = await getFile(data.payment?.paymentProof)
            const paymentTransaction: PaymentTransactionType = {
              date: new Date().toISOString(),
              idPaymentType: data.payment?.idPaymentTypeSubscription!,
              amountPaid: amountToPay ?? 0,
              idStudent: studentDebtData.idStudent,
              paymentProof: base64,
              idStudentDebt: resultCreateStudentDebt,
              idCompleted: true,
            }
            await mutationCreatePaymentTransaction.mutateAsync(paymentTransaction)
            studentDebtData.paymentProof = paymentTransaction.paymentProof
          }
        }
      } catch (error) {
        console.log(error)
      }
    }
    return studentDebtData;
  }


  const createNewDebtWithNewAmount = async (amountToPay: number, data: FormValues, subjects: string[]) => {
    let studentDebtData = {
      idStudent: studentData?.id ?? "",
      fullname: userData.name.concat(" ").concat(studentData?.schoolCode ?? "") ?? "",
      idPaymentType: data.payment?.idPaymentTypeSubscription ?? data.payment?.idPaymentTypePerSubject ?? "",
      currentAmount: amountToPay,
      dueDate: formatDateForInput(addDaysToDate(new Date(), 30).toISOString()),
      isPayed: stringToBoolean(data.payment?.isPaymentByEntityReference) ? false : true,
      description: paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.idPaymentTypePerSubject)?.description ?? paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.idPaymentTypePerSubject)?.description ?? "Inscrição",
      idSubscription: subscriptionData.id,
      idsSubjects: JSON.stringify(subjects),
      id: '',
      originalAmount: amountToPay,
      referenceCode: '',
      paymentProof: ''
    }
    const resultCreateStudentDebt = await mutationCreateStudentDebt.mutateAsync(studentDebtData);
    studentDebtData = { ...studentDebtData, id: resultCreateStudentDebt };
    if (stringToBoolean(getValues('payment.isPaymentByEntityReference'))) {
      const currentDate = new Date();
      try {
        let referenceCode = await getReferenceCodeV2({
          amount: studentDebtData.currentAmount,
          idStudent: studentDebtData.idStudent,
          idPaymentType: studentDebtData.idPaymentType,
          idStudentDebt: studentDebtData.id,
          month: (currentDate.getMonth() + 1).toString(),
          year: currentDate.getFullYear().toString(),
          description: studentDebtData.description,
          idBankEntity: data?.payment?.idBankEntity,
          entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity)
        });

        const paymentReference: PaymentReferenceType = {
          idStudent: studentDebtData.idStudent,
          idPaymentType: studentDebtData.idPaymentType,
          amount: studentDebtData.currentAmount,
          dueDate: new Date(studentDebtData.dueDate).toISOString(),
          referenceCode: referenceCode,
          idStudentDebt: studentDebtData.id,
          entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity),
          idBankEntity: data?.payment?.idBankEntity
        }
        await mutationSaveReferenceCode.mutateAsync(paymentReference)
        studentDebtData.referenceCode = referenceCode;
      } catch (ex: any) {
        toast.error(ex?.message);
      }
    } else {
      const { base64 } = await getFile(data.payment?.paymentProof)
      const paymentTransaction: PaymentTransactionType = {
        date: new Date().toISOString(),
        idPaymentType: data.payment?.idPaymentTypeSubscription!,
        amountPaid: amountToPay ?? 0,
        idStudent: studentDebtData.idStudent,
        paymentProof: base64,
        idStudentDebt: resultCreateStudentDebt,
        idCompleted: true,
      }
      await mutationCreatePaymentTransaction.mutateAsync(paymentTransaction)
      studentDebtData.paymentProof = paymentTransaction.paymentProof;
    }
    return studentDebtData;
  }

  const onSubmit = handleSubmit(async (data) => {
    const subjectsSubscriptions = data?.subjectsSubscriptions?.map(item => JSON.parse(item));
    const retrySubjectsReproved = data?.retrySubjectsReproved?.map(item => JSON.parse(item));
    let dataToPrint = { ...subscriptionData }
    let studentDebtData = {
      originalAmount: subscriptionData?.payment?.totalPrice,
      currentAmount: subscriptionData?.payment?.totalPrice,
      referenceCode: subscriptionData?.payment?.reference,
      paymentProof: subscriptionData?.payment?.paymentProof
    };

    try {
      await Promise.all(subscriptionData.planSubjects!.map((cv) => {
        if (cv) {
          const subscriptionSubject: SubscriptionSubjectType = {
            idSubject: cv.idSubject!,
            idSubscription: subscriptionData.id!,
            status: false,
            id: cv.id,
            level: cv?.level,
            semester: cv?.semester
          }
          return mutationUpdateSubscriptionSubject.mutateAsync(subscriptionSubject)
        }
      }))
    } catch (error: any) {
      toast.error(error?.message);
    }
    try {
      await Promise.all(subjectsSubscriptions!.map((cv) => {
        if (cv) {
          const isAbleToDoEqual = reprovedSubjectsData?.items.find(rs => rs.idSubject === cv.idSubjectPrecedence);
          if (!(isAbleToDoEqual && isAbleToDoEqual?.id)) {
            const subscriptionSubject: SubscriptionSubjectType = {
              idSubject: cv.idSubject!,
              idSubscription: subscriptionData.id!,
              status: true,
              level: cv?.level,
              semester: cv?.semester
            }
            return mutationCreateSubscriptionSubject.mutateAsync(subscriptionSubject)
          }
        }
      }))
    } catch (error: any) {
      toast.error(error?.message);
    }
    try {
      await Promise.all(retrySubjectsReproved!.map((cv) => {
        if (cv) {
          const isAbleToDoEqual = reprovedSubjectsData?.items.find(rs => rs.idSubject === cv.idSubjectPrecedence);
          if (!(isAbleToDoEqual && isAbleToDoEqual?.id)) {
            const subscriptionSubject: SubscriptionSubjectType = {
              idSubject: cv.idSubject!,
              idSubscription: subscriptionData.id!,
              status: true,
              level: cv?.level,
              semester: cv?.semester
            }
            return mutationCreateSubscriptionSubject.mutateAsync(subscriptionSubject)
          }
        }
      }))
    } catch (error: any) {
      toast.error(error?.message);
    }
    try {
      const subjects = [...subjectsSubscriptions, ...retrySubjectsReproved];
      const uniqueSubjects: {
        idSubject: string;
        idSubjectPrecedence?: string;
      }[] = Array.from(
        new Set(subjects?.map(subject => subject.idSubject))
      )?.map(idSubject =>
        subjects.find(subject => subject.idSubject === idSubject)!
      );
      const filteredSubjects = uniqueSubjects
        ?.filter(subject => {
          const isAbleToDoEqual = reprovedSubjectsData?.items.find(
            rs => rs.idSubject === subject.idSubjectPrecedence
          );
          return !(isAbleToDoEqual && isAbleToDoEqual.id);
        })
        ?.map(subject => subject.idSubject);

      let amountToPay = 0;
      const subjectsAmount = paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.idPaymentTypePerSubject!)?.amount ?? 0;
      const subscriptionPrice = paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.idPaymentTypeSubscription!)?.amount ?? 0;
      amountToPay += subscriptionPrice;

      const idsSubjectsFromStoraged = subscriptionData?.planSubjects;
      const sizeSubjectsFormSelected = filteredSubjects.length;
      const sizeSubjectsStorageOnSubscription = idsSubjectsFromStoraged && idsSubjectsFromStoraged?.length ? idsSubjectsFromStoraged?.length : 0;

      if (sizeSubjectsFormSelected > sizeSubjectsStorageOnSubscription) {
        const sizeSubjectAdded = Math.abs(sizeSubjectsFormSelected - sizeSubjectsStorageOnSubscription);
        const subjectsTotal = sizeSubjectAdded * subjectsAmount;
        amountToPay += subjectsTotal;
        if (amountToPay > 0) {
          studentDebtData = await createNewDebtWithNewAmount(amountToPay, data, filteredSubjects);
        }
      } else {
        const actualSizeSubject = Math.abs(sizeSubjectsStorageOnSubscription - sizeSubjectsFormSelected);
        amountToPay += actualSizeSubject * subjectsAmount;
        if (amountToPay > 0) {
          studentDebtData = await removeCurrentDebtAndCreateNewDebt(amountToPay, data, filteredSubjects)
        }
      }

    } catch (error) {
      console.error("Error processing subjects:", error);
    }

    dataToPrint.course = studentData?.course;
    dataToPrint.payment = {
      reference: studentDebtData?.referenceCode,
      paymentProof: data?.payment?.paymentProof ?? "",
      entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity),
      totalPrice: studentDebtData?.currentAmount
    }
    toast.success('Inscrição actualizada');
    await handlePrintSubscription(dataToPrint!);
  })
  const handlePrintSubscription = async (s: SubscriptionType) => {
    const data = await getSubjectsSubscriptions({
      PageSize: 400,
      PageNumber: 1,
      IdSubscription: s.id,
    }
    );
    const subscription = { ...s };
    subscription.planSubjects = data.items as any
    try {
      const studentDebtsResponse = await getStudentDebtsBySubscription(subscription.id!);
      const paymentsReferences = studentDebtsResponse
        ?.flatMap((sdr) => sdr.paymentReferences || [])
        ?.filter((ref) => ref?.id)
        ?.filter((ref) => ref.status);
      subscription.paymentsReferences = paymentsReferences;
      const paymentsTransactions = studentDebtsResponse
        ?.flatMap((sdr) => sdr.paymentTransactions || [])
        ?.filter((ref) => ref?.id)
        ?.filter((ref) => ref.status);
      subscription.paymentsTransactions = paymentsTransactions;
      const studentDebtsAssociations = studentDebtsResponse?.map(sdr => { return { idStudentDebt: sdr?.id, idSubscription: sdr?.idSubscription, idEnrollment: sdr?.idEnrollment, price: sdr?.originalAmount } })
      subscription.studentDebtsAssociations = studentDebtsAssociations;
      const idsSubjectsArray = Array.from(
        new Set(
          studentDebtsResponse
            ?.map((sdr) => JSON.parse(sdr?.idsSubjects || "[]"))
            .flat()
        )
      );
      subscription.idsSubjectsArray = idsSubjectsArray;
      const studentDebtData = studentDebtsResponse?.[0];
      const paymentReferences = studentDebtData?.paymentReferences;
      const paymentTransactions = studentDebtData?.paymentTransactions;
      const paymentTransaction = paymentTransactions?.find(p => p?.status);
      const paymentReference = paymentReferences?.find(p => p?.status);
      subscription.course = subscription?.course;
      subscription.idsSubjects = studentDebtData?.idsSubjects;
      subscription.payment = {
        idEnrollment: studentDebtData?.idEnrollment,
        entity: paymentReference?.entity,
        reference: paymentReference?.referenceCode,
        totalPrice: paymentReference?.amount,
        paymentProof: paymentTransaction?.paymentProof,
        idStudentDebt: studentDebtData?.id,
        isPaymentByEntityReference: true,
      }

    } catch (error) {
      console.log({ error })
    }
    dispatch(setSubscriptionData(subscription));
    dispatch(setPdfPreviewType('SUBSCRIPTION'));
    navigate(routes.pdfPreview);
  }
  const handleBack = async () => {
    dispatch(resetUserData());
    dispatch(resetStudentData());
    dispatch(resetSubscriptionData());
    navigate(routes.subscriptions);
  }

  const watchPaymentType = watch('payment.isPaymentByEntityReference');
  const handleOnChangePaymentType = (e: any) => {
    const paymentType = stringToBoolean(getValues('payment.isPaymentByEntityReference'))
    if (paymentType) {
      resetField('payment.paymentProof')
    }
    if (!paymentType) {
      resetField('payment.idBankEntity')
    }
  };
  return (
    <Flex direction="column" gap="2">

      <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
        <Callout.Root>
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            Valor a pagar :  {paymentTotal}
          </Callout.Text>
        </Callout.Root>
      </Flex>

      <form onSubmit={onSubmit} noValidate>

        <Card size="5">
          <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate) => {
            setActiveSegment(tabToNavigate);
            if (tabToNavigate.includes('subscription')) {
              fetchReprovedSubjects();
              refreshPlanSubjects();
            }

            if (tabToNavigate.includes('payments')) {

              const bankEntityDB = subscriptionData?.payment?.entity;

              if (bankEntityDB) {

                const schoolBankConfig = schoolBankConfigsData?.items?.find(
                  item => item.bankEntity == bankEntityDB
                );

                if (schoolBankConfig?.id) {
                  setValue("payment.idBankEntity", schoolBankConfig.id);
                } else {
                  setValue("payment.idBankEntity", "");
                }
              }
            }
          }}>
            <Tabs.List>
              <Tabs.Trigger value="user">Dados gerais</Tabs.Trigger>
              <Tabs.Trigger value="subscription">Disciplinas</Tabs.Trigger>
              <Tabs.Trigger value="payments">Pagamentos</Tabs.Trigger>
            </Tabs.List>
            <Box pt="3">
              <Tabs.Content value="user">
                <UserPreview />
              </Tabs.Content>
              <Tabs.Content value="subscription">
                <Flex direction={'column'} gap='4'>
                  <CheckboxGroupComponent
                    name="retrySubjectsReproved"
                    label="Disciplinas em atraso"
                    control={control}
                    errors={errors}
                    options={reprovedSubjectsData?.items && reprovedSubjectsData?.items?.sort((a, b) => a.level - b.level)?.map(reprovedSubjects => {
                      return ({ value: JSON.stringify(reprovedSubjects), label: `${reprovedSubjects.subject} - ${reprovedSubjects.semester}º Semestre` });
                    })}
                    style={{ width: '100%' }}
                    itemsError={isErrorReprovedSubjects}
                    itemsLoading={isLoadingReprovedSubjects}
                  />
                  <CheckboxGroupComponent
                    name="subjectsSubscriptions"
                    label="Disciplinas do semestre"
                    control={control}
                    errors={errors}
                    options={planSubjectsData?.items?.sort((a, b) => a.level - b.level)?.map(item => {
                      const isAbleToDoEqual = reprovedSubjectsData?.items.find(rs => rs.idSubject === item.idSubjectPrecedence);
                      return {
                        value: JSON.stringify({
                          id: item.id,
                          subject: item.subject,
                          semester: item.semester,
                          idSubject: item.idSubject,
                          idSubjectPrecedence: item.idSubjectPrecedence,
                          level: item.level,
                          // isAbleToDoEqual: isAbleToDoEqual && isAbleToDoEqual?.id, 
                          // isCore: item?.isCore
                        }),
                        label: `${item.subject} (${item.level}ºAno ${item.semester}ºSemestre)`.concat((item?.isCore ? (isAbleToDoEqual && isAbleToDoEqual?.id && " (Nuclear)  Não será inscrito") : (isAbleToDoEqual && isAbleToDoEqual?.id && " (Opcional)  Não será inscrito")) ?? ""),
                        isCoreBadge: item.isCore ? (
                          <>
                            <Badge color="green">Nuclear</Badge>
                            {isAbleToDoEqual && isAbleToDoEqual?.id && (
                              <Badge color="red">Não será inscrito</Badge>
                            )}
                          </>
                        ) : (
                          <>
                            <Badge color="gray">Opcional</Badge>
                            {isAbleToDoEqual && isAbleToDoEqual?.id && (
                              <Badge color="red">Não será inscrito</Badge>
                            )}
                          </>
                        ),
                      };
                    })}

                    style={{ width: '100%' }}
                    itemsError={isErrorPlanSubjectsData}
                    itemsLoading={isLoadingPlanSubjectsData}
                  />
                </Flex>

              </Tabs.Content>

              <Tabs.Content value='payments'>
                <Flex gap='6' direction='column'>

                  <Callout.Root>
                    <Callout.Icon>
                      <InfoCircledIcon />
                    </Callout.Icon>
                    <Callout.Text>
                      Valor a pagar :  {paymentTotal}
                    </Callout.Text>
                  </Callout.Root>

                  <RadioGroupComponent name="payment.isPaymentByEntityReference" label="Tipo de pagamento" control={control} errors={errors}
                    style={{ width: '50%' }}
                    options={[
                      { value: "true", label: "Entidade Referencia" }
                      , { value: "false", label: "Comprovativo" }]}
                    fieldProps={{
                      style: { display: 'flex', flexDirection: 'row', gap: '16px' },
                      onChange: handleOnChangePaymentType
                    }}
                  />
                  <SelectComponent
                    name="payment.idBankEntity"
                    label="Entidade Bancária"
                    control={control}
                    errors={errors}
                    itemsLoading={isLoadingSchoolschoolBankConfigs}
                    itemsError={isErrorSchoolschoolBankConfigs}
                    options={schoolBankConfigsData?.items?.map(item => ({
                      value: item.id!,
                      label: String(item.bankEntity!) ?? "",
                    })) ?? []}
                    style={{ width: '100%' }}
                    fieldProps={{ disabled: !stringToBoolean(watchPaymentType) }}
                  />
                  <Box>
                    <Text as="label" size="2">Comprovativo de Pagamento</Text>
                    <Controller
                      name={`payment.paymentProof`}
                      control={control}

                      disabled={stringToBoolean(watchPaymentType)}
                      render={({ field }) => (
                        <DragAndDropFileUpload
                          onFileDrop={(file) => {
                            field.onChange(file);
                          }}
                          label={`Arraste ou selecione o arquivo`}
                          accept="*"
                          selectedFile={field.value}
                          isReadOnly={stringToBoolean(watchPaymentType)}
                          disabled={stringToBoolean(watchPaymentType)}
                        />
                      )}
                    />
                    {errors.payment?.paymentProof && (
                      <Text color="red" size="1">{`${errors.payment.paymentProof.message}`}</Text>
                    )}
                  </Box>
                  <SelectComponent
                    name="payment.idPaymentTypeSubscription"
                    label="Valor da inscrição"
                    control={control}
                    errors={errors}
                    itemsLoading={isLoadingPaymentTypes}
                    itemsError={isErrorPaymentTypes}
                    options={paymentTypesData?.items?.filter(i => Number((i?.internalCode!)) === 26).map(item => ({
                      value: item.id!,
                      label: String(item.description!) ?? "",
                    })) ?? []}
                    style={{ width: '100%' }}
                  />
                  <SelectComponent
                    name="payment.idPaymentTypePerSubject"
                    label="Valor por disciplina"
                    control={control}
                    errors={errors}
                    itemsLoading={isLoadingPaymentTypes}
                    itemsError={isErrorPaymentTypes}
                    options={paymentTypesData?.items?.filter(i => Number((i?.internalCode!)) === 27).map(item => ({
                      value: item.id!,
                      label: String(item.description!) ?? "",
                    })) ?? []}
                    style={{ width: '100%' }}
                  />

                  <Callout.Root>
                    <Callout.Icon>
                      <InfoCircledIcon />
                    </Callout.Icon>
                    <Callout.Text>
                      Valor a pagar : {paymentTotal}
                    </Callout.Text>
                  </Callout.Root>
                </Flex>
              </Tabs.Content>
            </Box>
          </Tabs.Root>
        </Card>
        <Flex mt="4" justify="end" gap={'4'}>
          <Button color="red" onClick={handleBack} disabled={loadingState}>
            <ChevronLeftIcon /> Voltar
          </Button>
          <Button type="submit" variant="surface" disabled={loadingState}>
            {loadingState ? <Spinner loading /> : <EnterIcon />} Atualizar
          </Button>
        </Flex>
      </form>
    </Flex>

  );
};
